/**
 * THEME — "Dark Cyber-Minimalism" (v0.4 design refresh)
 *
 * Single source of truth for every color + layout token in the game. Mirrors
 * `design/handoff/DESIGN_TOKENS.md`. When adding a new UI element, pull the
 * token from here instead of hardcoding a hex literal — if we retheme again,
 * we retheme in one place.
 *
 * Each color token is exported twice:
 *   - `NAME`     — numeric (0xRRGGBB) for Phaser Graphics / setTint / setFillStyle
 *   - `NAME_CSS` — string "#RRGGBB" for Phaser Text `color:` fields
 *
 * This avoids the stringly-typed drift we used to get when scenes sprinkled
 * raw hex literals everywhere.
 */

// Helper: numeric → "#RRGGBB"
const hex = (n: number): string => `#${n.toString(16).padStart(6, '0').toUpperCase()}`;

// ============================================================================
// BACKGROUND TOKENS
// ============================================================================
export const BG_VOID       = 0x050507; // Deepest bg (outside phone frame) — bg-void
export const BG_VOID_2     = 0x0A0A0C; // Stage background — bg-void-2
export const BG_SURFACE    = 0x0A0B11; // Frosted panel base
export const BG_PANEL      = 0x0D0E14; // Mid-depth panel (glass panel @ 0.82 alpha) — bg-panel
export const BG_CHIP       = 0x151821; // Top-bar icon buttons — bg-chip (alpha 0.70)
export const BG_ELEVATED   = 0x12151E; // Selected / highlighted surface
export const BG_CELL       = 0x0F1115; // Grid cell background
export const BG_HOVER      = 0x151821; // Pressed / hover state
export const BG_MUTED_WARM = 0x1E2430; // Ambient particle color (menu background)

export const BG_VOID_CSS       = hex(BG_VOID);
export const BG_VOID_2_CSS     = hex(BG_VOID_2);
export const BG_SURFACE_CSS    = hex(BG_SURFACE);
export const BG_PANEL_CSS      = hex(BG_PANEL);
export const BG_CHIP_CSS       = hex(BG_CHIP);
export const BG_ELEVATED_CSS   = hex(BG_ELEVATED);
export const BG_CELL_CSS       = hex(BG_CELL);
export const BG_HOVER_CSS      = hex(BG_HOVER);

/** Alpha companions for rgba() usage. */
export const BG_PANEL_ALPHA      = 0.82; // glass panel default opacity
export const BG_PANEL_LITE_ALPHA = 0.60; // softer panels
export const BG_CHIP_ALPHA       = 0.70; // chip / icon button opacity
export const SURFACE_HOVER_ALPHA = 0.15; // secondary button bg opacity

// ============================================================================
// ACCENT TOKENS
// ============================================================================
export const ACCENT_CYAN       = 0x00F0FF; // Primary accent — buttons, overcharge charging, highlights
export const ACCENT_CYAN_DEEP  = 0x0066FF; // Gradient pair for cyan
export const ACCENT_PURPLE     = 0x8A00FF; // Premium / Overcharge ready state
export const ACCENT_MAGENTA    = 0xFF003C; // Glitch danger — corruption, errors
export const ACCENT_GOLD       = 0xFFD600; // Warnings, combo counters, currency
export const ACCENT_GREEN      = 0x22FFA7; // Positive feedback — healing pulse, success

export const ACCENT_CYAN_CSS    = hex(ACCENT_CYAN);
export const ACCENT_PURPLE_CSS  = hex(ACCENT_PURPLE);
export const ACCENT_MAGENTA_CSS = hex(ACCENT_MAGENTA);
export const ACCENT_GOLD_CSS    = hex(ACCENT_GOLD);
export const ACCENT_GREEN_CSS   = hex(ACCENT_GREEN);

// ============================================================================
// TEXT / INK TOKENS
// ============================================================================
export const TEXT_PRIMARY    = 0xF5F7FA; // Body text, headings — ink-primary. Pure white #FFFFFF still banned.
export const TEXT_SECONDARY  = 0x8892B0; // Labels, subtitles — ink-secondary
export const TEXT_TERTIARY   = 0x495670; // Hints, placeholders, "/X" denominator — ink-tertiary
export const TEXT_WARM       = 0xC8D0DC; // Softer body (lore italics) — ink-warm
export const TEXT_DISABLED   = 0x1A1F2A; // Fully de-emphasized

export const TEXT_PRIMARY_CSS   = hex(TEXT_PRIMARY);
export const TEXT_SECONDARY_CSS = hex(TEXT_SECONDARY);
export const TEXT_TERTIARY_CSS  = hex(TEXT_TERTIARY);
export const TEXT_WARM_CSS      = hex(TEXT_WARM);

// ============================================================================
// BORDER TOKENS — rgb triplets for rgba() usage on DOM / Graphics alpha strokes
// ============================================================================
/** Soft panel borders — border-soft. Use as rgba(73, 86, 112, 0.20). */
export const BORDER_SOFT      = 0x495670;
export const BORDER_SOFT_ALPHA = 0.20;
/** Default chip borders — border-default. Use as rgba(73, 86, 112, 0.30). */
export const BORDER_DEFAULT      = 0x495670;
export const BORDER_DEFAULT_ALPHA = 0.30;

// ============================================================================
// RADII SCALE (px) — mirrors --r-xs … --r-3xl in DESIGN_TOKENS.md
// ============================================================================
export const R_XS  = 4;   // Tier badges
export const R_SM  = 6;   // Small badges, mini-glyph tiles
export const R_MD  = 8;   // Game tiles, resource pills
export const R_LG  = 10;  // Icon buttons, chips
export const R_XL  = 12;  // Resource cards
export const R_2XL = 14;  // Main cards, CTAs
export const R_3XL = 20;  // Modals

// ============================================================================
// MOTION TOKENS (ms) — mirrors the motion table in DESIGN_TOKENS.md
// ============================================================================
export const DUR_TAP              = 150;
export const DUR_TOAST            = 240;
export const DUR_MODAL_ENTRY      = 380; // within 320–420 range
export const DUR_MERGE_FLASH      = 600;
export const DUR_SPLINTER_POP     = 500;
export const DUR_DISCOVERY_BURST  = 1200;
export const DUR_TILE_FLOAT       = 3200; // idle float, infinite
export const DUR_TILE_AURA        = 1800; // high-tier aura pulse, infinite
export const DUR_READY_PULSE      = 1400; // splinter-ready meter, infinite
export const DUR_T7_ORBIT         = 10000; // particle orbit, infinite linear

// ============================================================================
// TYPOGRAPHY STACK
// ============================================================================
// Display / headers → Space Grotesk (futuristic geometric sans)
// Body / labels     → Inter
// Numeric HUD       → JetBrains Mono
// Arial / system-ui remain BANNED.
export const FONT_DISPLAY = '"Space Grotesk", Inter, sans-serif';
export const FONT_BODY    = 'Inter, "Space Grotesk", sans-serif';
export const FONT_MONO    = '"JetBrains Mono", monospace';

// ============================================================================
// SHORTHAND — for the patterns we use over and over
// ============================================================================

/** Standard frosted-glass panel stroke + fill values (used by FrostedPanel). */
export const GLASS_FILL        = BG_PANEL;
export const GLASS_FILL_ALPHA  = 0.72;
export const GLASS_STROKE      = ACCENT_CYAN;
export const GLASS_STROKE_ALPHA = 0.35;
export const GLASS_CORNER      = 14;

/** Spring easing shorthand — use these instead of Cubic.Out for most UI. */
export const SPRING_IN  = 'Back.Out';
export const SPRING_OUT = 'Back.In';
